import React, { useState } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Checkbox } from "@/components/wpl/checkbox/src";
import { DropdownSelector } from "@/components/wpl/dropdown/src";
import { TextInput } from "@/components/wpl/text-input/src";
import { wacToken } from "@/libraries/design/foundations";

import {
    UserSegmentTypes,
    ProductRecommendationOptions
} from "./use-user-segments";
export interface PersonalizationUserSegmentValues {
    [UserSegmentTypes.LOGIN_STATUS]?: string;
    [UserSegmentTypes.GEOLOCATION]?: string;
    [UserSegmentTypes.JOB_FUNCTION]?: string;
    [UserSegmentTypes.DOMAIN_TYPE]?: string;
    [UserSegmentTypes.OWNED_PRODUCTS]?: string[];
    [UserSegmentTypes.CLUSTER_TAG]?: string;
    [UserSegmentTypes.FIRST_RECOMMENDATION]?: ProductRecommendationOptions;
    [UserSegmentTypes.SECOND_RECOMMENDATION]?: ProductRecommendationOptions;
    [UserSegmentTypes.THIRD_RECOMMENDATION]?: ProductRecommendationOptions;
    [UserSegmentTypes.FOURTH_RECOMMENDATION]?: ProductRecommendationOptions;
    useOverrides: boolean;
    enrollInExperiment: boolean;
}

const JOB_FUNCTION = [
    { value: "engineering", label: "Engineering" },
    { value: "product management", label: "Product Management" },
    { value: "design", label: "Design" },
    { value: "data science", label: "Data Science" },
    { value: "marketing", label: "Marketing" },
    { value: "sales", label: "Sales" },
    { value: "human resources", label: "Human Resources" },
    { value: "finance", label: "Finance" },
    { value: "operations", label: "Operations" },
    { value: "legal", label: "Legal" },
    { value: "customer success", label: "Customer Success" },
    { value: "executive", label: "Executive" },
    { value: "other", label: "Other" },
    { value: "information technology", label: "Information Technology" },
    { value: "customer service", label: "Customer Service" },
    { value: "program management", label: "Program Management" },
    { value: "c-suite", label: "C-Suite" }
];

const PRODUCT_OPTIONS = [
    { value: "jira-software", label: "Jira" },
    { value: "confluence", label: "Confluence" },
    { value: "jira-servicedesk", label: "JSM" },
    { value: "loom", label: "Loom" },
    { value: "jira-product-discovery", label: "JPD" },
    { value: "rovo.ondemand", label: "Rovo" }
];

interface PersonalizationOverrideControlsProps {
    values: PersonalizationUserSegmentValues;
    setValues: React.Dispatch<
        React.SetStateAction<PersonalizationUserSegmentValues>
    >;
    renderMessage: string;
    isContainerHovered: boolean;
}

const PersonalizationControlPanel = ({
    values,
    handleSelectChange
}: {
    values: PersonalizationUserSegmentValues;
    handleSelectChange: (
        field: keyof PersonalizationUserSegmentValues,
        value: string | boolean | string[]
    ) => void;
}) => (
    <div
        css={css({
            display: "flex",
            flexDirection: "column",
            gap: token("space.100")
        })}
    >
        <div
            onClick={(e) => {
                const target = e.target as HTMLInputElement;
                if (target.type === "checkbox") {
                    handleSelectChange("enrollInExperiment", target.checked);
                }
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelectChange(
                        "enrollInExperiment",
                        !values.enrollInExperiment
                    );
                }
            }}
            role="button"
            tabIndex={0}
        >
            <Checkbox
                id="enrollInExperiment"
                name="enrollInExperiment"
                value="enrollInExperiment"
                label="Enroll in Experiment Treatment?"
                checked={values.enrollInExperiment}
            />
        </div>
        <div
            onClick={(e) => {
                const target = e.target as HTMLInputElement;
                if (target.type === "checkbox") {
                    handleSelectChange("useOverrides", target.checked);
                }
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelectChange("useOverrides", !values.useOverrides);
                }
            }}
            role="button"
            tabIndex={0}
        >
            <Checkbox
                id="useOverrides"
                name="useOverrides"
                value="useOverrides"
                label="Use Overrides?"
                checked={values.useOverrides}
            />
        </div>
        <div>
            <DropdownSelector
                name={UserSegmentTypes.LOGIN_STATUS}
                id={UserSegmentTypes.LOGIN_STATUS}
                label="Login Status"
                options={[
                    { value: "loggedIn", label: "Logged In" },
                    { value: "loggedOut", label: "Logged Out" }
                ]}
                defaultValue={{
                    value: values[UserSegmentTypes.LOGIN_STATUS] || "",
                    label:
                        values[UserSegmentTypes.LOGIN_STATUS] === "loggedIn"
                            ? "Logged In"
                            : "Logged Out"
                }}
                onChangeHandler={(value) => {
                    handleSelectChange(
                        UserSegmentTypes.LOGIN_STATUS,
                        value as string
                    );
                }}
            />
        </div>
        <div>
            <TextInput
                id="geolocation"
                name={UserSegmentTypes.GEOLOCATION}
                label="Geolocation"
                placeholder="e.g. US, UK, AU"
                value={values[UserSegmentTypes.GEOLOCATION]}
                onChangeHandler={(value) => {
                    handleSelectChange(
                        UserSegmentTypes.GEOLOCATION,
                        value as string
                    );
                }}
            />
        </div>
        <div>
            <DropdownSelector
                name={UserSegmentTypes.JOB_FUNCTION}
                id={UserSegmentTypes.JOB_FUNCTION}
                label="Job Function"
                options={JOB_FUNCTION.map((entry) => ({
                    value: entry.value,
                    label: entry.label
                }))}
                defaultValue={{
                    value: values[UserSegmentTypes.JOB_FUNCTION] || "",
                    label:
                        JOB_FUNCTION.find((e) => {
                            return (
                                e.value ===
                                values[UserSegmentTypes.JOB_FUNCTION]
                            );
                        })?.label || "test"
                }}
                onChangeHandler={(value) => {
                    handleSelectChange(
                        UserSegmentTypes.JOB_FUNCTION,
                        value as string
                    );
                }}
            />
        </div>
        <div>
            <DropdownSelector
                name={UserSegmentTypes.DOMAIN_TYPE}
                id={UserSegmentTypes.DOMAIN_TYPE}
                label="Domain Type"
                options={[
                    { value: "BUSINESS", label: "Business" },
                    { value: "PERSONAL", label: "Personal" }
                ]}
                defaultValue={{
                    value: values[UserSegmentTypes.DOMAIN_TYPE] || "",
                    label:
                        values[UserSegmentTypes.DOMAIN_TYPE] === "BUSINESS"
                            ? "Business"
                            : "Personal"
                }}
                onChangeHandler={(value) => {
                    handleSelectChange(
                        UserSegmentTypes.DOMAIN_TYPE,
                        value as string
                    );
                }}
            />
        </div>
        <div>
            <label
                css={css({
                    display: "block",
                    color: wacToken("color.neutral.dark200"),
                    fontSize: "0.75rem",
                    lineHeight: "1.1",
                    marginBlockEnd: token("space.050"),
                    textAlign: "left"
                })}
                htmlFor={UserSegmentTypes.OWNED_PRODUCTS}
            >
                Owned Products
            </label>
            <div
                css={css({
                    display: "flex",
                    flexWrap: "wrap",
                    gap: token("space.050"),
                    padding: token("space.100"),
                    border: `1px solid ${wacToken("color.neutral.dark700")}`,
                    borderRadius: "4px",
                    minHeight: "40px",
                    alignItems: "center"
                })}
            >
                {[
                    { value: "jira-software", label: "Jira" },
                    { value: "confluence", label: "Confluence" },
                    { value: "bitbucket", label: "Bitbucket" },
                    { value: "compass", label: "Compass" },
                    {
                        value: "jira-product-discovery",
                        label: "Jira Product Discovery"
                    },
                    { value: "statuspage", label: "Statuspage" },
                    { value: "loom", label: "Loom" },
                    { value: "compass", label: "Compass" },
                    { value: "rovo.ondemand", label: "Rovo" },
                    { value: "trello", label: "Trello" }
                ].map((product) => {
                    const isSelected = Array.isArray(
                        values[UserSegmentTypes.OWNED_PRODUCTS]
                    )
                        ? values[UserSegmentTypes.OWNED_PRODUCTS]?.includes(
                              product.value
                          )
                        : values[UserSegmentTypes.OWNED_PRODUCTS] ===
                          product.value;

                    return (
                        <button
                            key={product.value}
                            type="button"
                            onClick={() => {
                                const currentProducts = Array.isArray(
                                    values[UserSegmentTypes.OWNED_PRODUCTS]
                                )
                                    ? values[UserSegmentTypes.OWNED_PRODUCTS] ||
                                      []
                                    : values[UserSegmentTypes.OWNED_PRODUCTS]
                                    ? [values[UserSegmentTypes.OWNED_PRODUCTS]]
                                    : [];

                                let updatedProducts: string[];
                                if (isSelected) {
                                    updatedProducts = currentProducts.filter(
                                        (p) => p !== product.value
                                    );
                                } else {
                                    updatedProducts = [
                                        ...currentProducts,
                                        product.value
                                    ];
                                }

                                handleSelectChange(
                                    UserSegmentTypes.OWNED_PRODUCTS,
                                    updatedProducts
                                );
                            }}
                            css={css({
                                "padding": `${token("space.050")} ${token(
                                    "space.100"
                                )}`,
                                "backgroundColor": isSelected
                                    ? wacToken("color.extended.blue300")
                                    : wacToken("color.neutral.light200"),
                                "color": isSelected
                                    ? wacToken("color.neutral.light0")
                                    : wacToken("color.neutral.dark200"),
                                "border": "none",
                                "borderRadius": "4px",
                                "cursor": "pointer",
                                "fontSize": "0.875rem",
                                ":hover": {
                                    backgroundColor: isSelected
                                        ? wacToken("color.extended.blue400")
                                        : wacToken("color.neutral.light300")
                                }
                            })}
                        >
                            {product.label}
                        </button>
                    );
                })}
            </div>
        </div>
        <div>
            <label
                css={css({
                    display: "block",
                    color: wacToken("color.neutral.dark200"),
                    fontSize: "0.75rem",
                    lineHeight: "1.1",
                    marginBlockEnd: token("space.050"),
                    textAlign: "left"
                })}
                htmlFor={UserSegmentTypes.CLUSTER_TAG}
            >
                Anchor/Cluster Tag
            </label>
            <input
                id={UserSegmentTypes.CLUSTER_TAG}
                data-testid={UserSegmentTypes.CLUSTER_TAG}
                css={[css({ width: "100%" })]}
                type="text"
                value={values[UserSegmentTypes.CLUSTER_TAG]}
                onChange={(event) => {
                    handleSelectChange(
                        UserSegmentTypes.CLUSTER_TAG,
                        event.target.value.toLowerCase()
                    );
                }}
            />
        </div>
        {(
            [
                {
                    segmentType: UserSegmentTypes.FIRST_RECOMMENDATION,
                    label: "First Recommendation",
                    defaultValue: "jira",
                    defaultLabel: "Jira"
                },
                {
                    segmentType: UserSegmentTypes.SECOND_RECOMMENDATION,
                    label: "Second Recommendation",
                    defaultValue: "confluence",
                    defaultLabel: "Confluence"
                },
                {
                    segmentType: UserSegmentTypes.THIRD_RECOMMENDATION,
                    label: "Third Recommendation",
                    defaultValue: "jsm",
                    defaultLabel: "JSM"
                },
                {
                    segmentType: UserSegmentTypes.FOURTH_RECOMMENDATION,
                    label: "Fourth Recommendation",
                    defaultValue: "loom",
                    defaultLabel: "Loom"
                }
            ] as const
        ).map(({ segmentType, label, defaultValue, defaultLabel }) => {
            const selectedValue = values[segmentType] || defaultValue;
            const selectedOption = PRODUCT_OPTIONS.find(
                (opt) => opt.value === selectedValue
            );
            return (
                <div key={segmentType}>
                    <DropdownSelector
                        name={segmentType}
                        id={segmentType}
                        label={label}
                        options={PRODUCT_OPTIONS}
                        defaultValue={{
                            value: selectedValue,
                            label: selectedOption?.label || defaultLabel
                        }}
                        onChangeHandler={(value) => {
                            handleSelectChange(segmentType, value as string);
                        }}
                    />
                </div>
            );
        })}
    </div>
);

export const PersonalizationOverrideControls = ({
    values,
    setValues,
    renderMessage,
    isContainerHovered
}: PersonalizationOverrideControlsProps) => {
    const [isVisible, setIsVisible] = useState(false);

    const handleSelectChange = (
        field: keyof PersonalizationUserSegmentValues,
        value: string | boolean | string[]
    ) => {
        setValues({ ...values, [field]: value });
    };

    // Only show the dog-ear when container is hovered
    if (!isContainerHovered) {
        return null;
    }

    return (
        <div
            css={css({
                position: "absolute",
                insetBlockStart: "-2px",
                insetInlineStart: "-2px",
                width: isVisible ? "320px" : "auto",
                height: isVisible ? "400px" : "auto",
                zIndex: 10
            })}
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
            onFocus={() => setIsVisible(true)}
            onBlur={() => setIsVisible(false)}
            role="button"
            tabIndex={0}
            data-testid="personalization-controls"
        >
            {/* Dog-ear Corner */}
            <div
                css={css({
                    position: "absolute",
                    insetBlockStart: 0,
                    insetInlineStart: 0,
                    zIndex: 15,
                    width: "48px",
                    height: "48px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    opacity: isContainerHovered ? 1 : 0,
                    transform: isContainerHovered ? "scale(1)" : "scale(0.8)"
                })}
                title="Personalization Controls"
            >
                {/* Rounded corner background */}
                <div
                    css={css({
                        "position": "absolute",
                        "insetBlockStart": 0,
                        "insetInlineStart": 0,
                        "width": "48px",
                        "height": "48px",
                        "backgroundColor": wacToken("color.extended.blue300"),
                        "borderRadius": `${wacToken(
                            "corner.radius.card"
                        )} 0 ${wacToken("corner.radius.card")} 0`,
                        "transition": "all 0.2s ease",
                        "boxShadow": `0 2px 4px ${wacToken(
                            "color.neutral.dark800"
                        )}`,
                        ":hover": {
                            backgroundColor: wacToken("color.extended.blue400")
                        }
                    })}
                />

                {/* Icon */}
                <div
                    css={css({
                        position: "absolute",
                        insetBlockStart: "50%",
                        insetInlineStart: "50%",
                        transform: "translate(-50%, -50%)",
                        color: wacToken("color.neutral.light0"),
                        fontSize: "16px",
                        lineHeight: 1,
                        pointerEvents: "none",
                        textShadow: `0 1px 2px ${wacToken(
                            "color.neutral.dark800"
                        )}`
                    })}
                >
                    👥
                </div>
            </div>

            {/* Control Panel */}
            {isVisible && (
                <div
                    css={css({
                        position: "absolute",
                        insetBlockStart: "52px",
                        insetInlineStart: "8px",
                        width: "280px",
                        zIndex: 20,
                        padding: token("space.200"),
                        backgroundColor: wacToken("color.neutral.light0"),
                        borderRadius: wacToken("corner.radius.card"),
                        boxShadow: `0 4px 12px ${wacToken(
                            "color.neutral.dark800"
                        )}`,
                        border: `1px solid ${wacToken("color.neutral.dark700")}`
                    })}
                >
                    <h3
                        css={css({
                            margin: `0 0 ${token("space.100")} 0`,
                            fontSize: wacToken("font.charlie.text"),
                            color: wacToken("color.neutral.dark200")
                        })}
                    >
                        Personalization Controls
                    </h3>

                    <div
                        css={css({
                            margin: `0 0 ${token("space.150")} 0`,
                            padding: token("space.075"),
                            fontSize: "0.75rem",
                            color: wacToken("color.neutral.dark300"),
                            backgroundColor: wacToken(
                                "color.extended.yellow100"
                            ),
                            borderRadius: "4px",
                            border: `1px solid ${wacToken(
                                "color.extended.yellow300"
                            )}`
                        })}
                    >
                        These controls and the blue border are only visible in
                        pre-production environments
                    </div>

                    <PersonalizationControlPanel
                        values={values}
                        handleSelectChange={handleSelectChange}
                    />

                    <div
                        css={css({
                            marginBlockStart: token("space.150"),
                            padding: token("space.100"),
                            backgroundColor: wacToken("color.extended.blue100"),
                            color: wacToken("color.neutral.dark300")
                        })}
                    >
                        {renderMessage}
                    </div>
                </div>
            )}
        </div>
    );
};
